import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  AddBox as UploadIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishedIcon,
  Folder as FolderIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useProjectProgress } from '../../contexts/ProjectProgressContext';
import useApi from '../../hooks/useApi';
import { useCustomAlert } from '../../components/common/CustomAlert';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';
import { canAccessDashboardRoutes } from '../../constants/dashboardAccessPermissions';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../../components/common';
import './ProjectsPage.css';

const ProjectsPage = () => {
  const navigate = useNavigate();
  const api = useApi();
  const { hasPermission, currentTenant } = useTenant();
  const { initializeProject } = useProjectProgress();
  const { showAlert, showConfirm } = useCustomAlert();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    projectStep: '',
  });
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishProject, setPublishProject] = useState(null);
  const [publishFolderIds, setPublishFolderIds] = useState([]);
  const [publishSubmitting, setPublishSubmitting] = useState(false);
  const [publishFoldersFlat, setPublishFoldersFlat] = useState([]);
  const [publishFoldersLoading, setPublishFoldersLoading] = useState(false);
  const [publishUserId, setPublishUserId] = useState(null);
  const [publishNewFolderName, setPublishNewFolderName] = useState('');
  const [publishCreateParentId, setPublishCreateParentId] = useState('');
  const [publishCreateSubmitting, setPublishCreateSubmitting] = useState(false);
  const [publishCreateError, setPublishCreateError] = useState(null);
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (publishCreateError) {
      showErrorToast(publishCreateError);
      setPublishCreateError(null);
    }
  }, [publishCreateError, showErrorToast]);

  useEffect(() => {
    if (canAccessDashboardRoutes(hasPermission)) {
      loadProjects();
    }
  }, [hasPermission]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await api.get('/projects');
      if (response.data?.success) {
        const data = response.data.data;
        let projectsArray = [];
        
        // Handle different response structures
        if (Array.isArray(data)) {
          projectsArray = data;
        } else if (data && Array.isArray(data.projects)) {
          projectsArray = data.projects;
        }
        
        // Map API property names (PascalCase) to frontend property names (camelCase)
        const mappedProjects = projectsArray.map(project => ({
          projectId: project.ProjectId || project.projectId,
          projectName: project.ProjectName || project.projectName,
          status: project.Status || project.status,
          projectStep: project.ProjectStep || project.projectStep,
          createdDate: project.CreatedDate || project.createdDate,
          lastModifiedDate: project.LastModifiedDate || project.lastModifiedDate,
          lastAccessedDate: project.LastAccessedDate || project.lastAccessedDate,
          isPublished: !!(project.IsPublished ?? project.isPublished),
        }));
        
        setProjects(mappedProjects);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'inprogress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'deleted':
        return 'Deleted';
      default:
        return status || 'Unknown';
    }
  };

  const getProjectStepLabel = (step) => {
    switch (step?.toLowerCase()) {
      case 'preview':
        return 'Data';
      case 'filter':
        return 'Filters';
      case 'cards':
        return 'Cards';
      case 'dashboard':
        return 'Dashboard';
      default:
        return step || 'Unknown';
    }
  };

  const handleViewProject = async (project) => {
    // Navigate to the appropriate step based on ProjectStep
    const step = project.projectStep?.toLowerCase() || 'preview';
    
    // Use projectId or fileName as identifier
    const identifier = project.projectId || project.fileName;
    
    if (!identifier) {
      showAlert('Unable to find project identifier. Please try again.', { severity: 'error' });
      return;
    }
    
    // Store identifier in localStorage for the current session
    localStorage.setItem('currentIdentifier', identifier);
    
    // Initialize project progress tracking with project data
    // If project already exists (opened from list), mark as saved so "Don't Save" won't soft-delete it
    if (project.projectId) {
      // Project exists - initialize with project data (opened from list = already saved)
      await initializeProject({
        projectId: project.projectId,
        projectName: project.projectName,
        fileName: project.fileName,
        displayName: project.displayName || project.projectName,
        currentStep: step,
      }, { markAsSaved: true });
    } else {
      // Project doesn't exist yet - use fileName/tableName
      await initializeProject({
        sessionId: identifier,
        fileName: identifier,
        projectName: project.projectName,
        displayName: project.displayName || project.projectName,
        currentStep: step,
      });
    }
    
    // Navigate to the appropriate step with identifier
    navigateToStep(step, identifier);
  };

  const navigateToStep = (step, identifier) => {
    // Identifier can be projectId, fileName, or tableName
    switch (step) {
      case 'preview':
        navigate(`/preview/${identifier}`);
        break;
      case 'filter':
        navigate(`/filters/${identifier}`);
        break;
      case 'cards':
        navigate(`/cards/${identifier}`);
        break;
      case 'dashboard':
        navigate(`/dashboard/${identifier}`);
        break;
      default:
        navigate(`/preview/${identifier}`);
    }
  };

  // Filter projects based on search text and column filters
  const filteredProjects = useMemo(() => {
    let filtered = [...projects];

    // Apply search text filter (searches across project name)
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(project =>
        project.projectName?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(project =>
        project.status?.toLowerCase() === filters.status.toLowerCase()
      );
    }

    // Apply project step filter
    if (filters.projectStep) {
      filtered = filtered.filter(project =>
        project.projectStep?.toLowerCase() === filters.projectStep.toLowerCase()
      );
    }

    return filtered;
  }, [projects, searchText, filters]);

  // Get unique values for filter dropdowns
  const uniqueStatuses = useMemo(() => {
    const statuses = [...new Set(projects.map(p => p.status).filter(Boolean))];
    return statuses.sort();
  }, [projects]);

  const uniqueProjectSteps = useMemo(() => {
    const steps = [...new Set(projects.map(p => p.projectStep).filter(Boolean))];
    return steps.sort();
  }, [projects]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const clearFilters = () => {
    setSearchText('');
    setFilters({
      status: '',
      projectStep: '',
    });
  };

  function buildFolderTree(flatList) {
    if (!flatList?.length) return [];
    const byId = {};
    flatList.forEach((f) => {
      byId[f.FolderId] = { ...f, children: [] };
    });
    const roots = [];
    flatList.forEach((f) => {
      const node = byId[f.FolderId];
      if (!f.ParentFolderId || !byId[f.ParentFolderId]) roots.push(node);
      else byId[f.ParentFolderId].children.push(node);
    });
    return roots;
  }

  function flattenFoldersForPicker(nodes, list = []) {
    nodes.forEach((n) => {
      list.push(n);
      if (n.children?.length) flattenFoldersForPicker(n.children, list);
    });
    return list;
  }

  const loadPublishFolders = useCallback(async () => {
    setPublishFoldersLoading(true);
    try {
      const res = await api.get('/folder');
      const raw = res.data?.data;
      const list = Array.isArray(raw) ? raw : [];
      const tree = buildFolderTree(list);
      setPublishFoldersFlat(flattenFoldersForPicker(tree));
    } catch (e) {
      console.error('Load folders failed:', e);
      setPublishFoldersFlat([]);
    } finally {
      setPublishFoldersLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!publishDialogOpen || !currentTenant?.tenantId) return;
    (async () => {
      try {
        const res = await api.get('/auth', { headers: { 'X-Tenant-ID': currentTenant.tenantId } });
        if (res.data?.success && res.data?.data?.user?.userId) {
          setPublishUserId(res.data.data.user.userId);
        }
      } catch (e) {
        console.warn('Could not load user for folder creation:', e);
      }
    })();
  }, [publishDialogOpen, currentTenant?.tenantId, api]);

  const handlePublishCreateFolder = async () => {
    if (!publishNewFolderName.trim() || !publishUserId) return;
    setPublishCreateSubmitting(true);
    setPublishCreateError(null);
    try {
      const res = await api.post('/folder', {
        FolderName: publishNewFolderName.trim(),
        CreatedBy: publishUserId,
        ...(publishCreateParentId && { ParentFolderId: publishCreateParentId }),
      });
      const payload = res.data?.data;
      const row = Array.isArray(payload) ? payload[0] : payload;
      const newFolderId = row?.FolderId || row?.folderId;
      setPublishNewFolderName('');
      await loadPublishFolders();
      if (newFolderId) {
        setPublishFolderIds((prev) =>
          prev.includes(newFolderId) ? prev : [...prev, newFolderId]
        );
      }
    } catch (e) {
      setPublishCreateError(getApiErrorMessage(e, 'Failed to create folder.'));
    } finally {
      setPublishCreateSubmitting(false);
    }
  };

  const handlePublishClick = async (project) => {
    setPublishProject(project);
    setPublishFolderIds([]);
    setPublishNewFolderName('');
    setPublishCreateParentId('');
    setPublishCreateError(null);
    setPublishDialogOpen(true);
    await loadPublishFolders();
  };

  const handlePublishDialogClose = () => {
    setPublishDialogOpen(false);
    setPublishProject(null);
    setPublishFolderIds([]);
    setPublishNewFolderName('');
    setPublishCreateParentId('');
    setPublishCreateError(null);
  };

  const togglePublishFolder = (folderId) => {
    setPublishFolderIds((prev) =>
      prev.includes(folderId) ? prev.filter((id) => id !== folderId) : [...prev, folderId]
    );
  };

  const handlePublishSubmit = async () => {
    if (!publishProject?.projectId) return;
    setPublishSubmitting(true);
    try {
      await api.post(`/projects/${publishProject.projectId}/publish`, {
        folderIds: publishFolderIds,
      });
      handlePublishDialogClose();
      loadProjects();
    } catch (e) {
      console.error('Publish failed:', e);
    } finally {
      setPublishSubmitting(false);
    }
  };

  const handleUnpublish = async (project) => {
    if (!project?.projectId) return;
    try {
      await api.post(`/projects/${project.projectId}/unpublish`);
      loadProjects();
    } catch (e) {
      console.error('Unpublish failed:', e);
    }
  };

  const handleDeleteProject = async (project) => {
    if (!project?.projectId) return;
    const confirmed = await showConfirm(`Are you sure you want to delete "${project.projectName || 'Untitled Project'}"?`, { title: 'Delete Project', confirmText: 'Delete' });
    if (!confirmed) return;
    try {
      await api.delete(`/projects/${project.projectId}`);
    } catch (e) {
      console.error('Delete failed:', e);
    } finally {
      loadProjects();
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontFamily: FONT_FAMILY_PRIMARY,
            fontWeight: 600,
          }}
        >
          Projects
        </Typography>
        {hasPermission('upload_data') && (
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => navigate('/landing')}
            sx={{
              backgroundColor: '#000000',
              '&:hover': {
                backgroundColor: '#333333',
              },
            }}
          >
            Create New Project
          </Button>
        )}
      </Box>

      {!loading && projects.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
          <UploadIcon sx={{ fontSize: 64, color: '#CCCCCC', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No projects yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload your first survey data file to get started
          </Typography>
          {hasPermission('upload_data') && (
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => navigate('/landing')}
            >
              Upload Project
            </Button>
          )}
        </Paper>
      ) : (
        <>
          {/* Search and Filter Bar */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search projects..."
                variant="outlined"
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchText && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchText('')}
                        edge="end"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 250, flexGrow: 1 }}
              />
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">
                    <em>All Statuses</em>
                  </MenuItem>
                  {uniqueStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Current Step</InputLabel>
                <Select
                  value={filters.projectStep}
                  label="Current Step"
                  onChange={(e) => handleFilterChange('projectStep', e.target.value)}
                >
                  <MenuItem value="">
                    <em>All Steps</em>
                  </MenuItem>
                  {uniqueProjectSteps.map((step) => (
                    <MenuItem key={step} value={step}>
                      {getProjectStepLabel(step)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(searchText || filters.status || filters.projectStep) && (
                <Button
                  size="small"
                  onClick={clearFilters}
                  startIcon={<ClearIcon />}
                  variant="outlined"
                >
                  Clear Filters
                </Button>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                Showing {filteredProjects.length} of {projects.length} projects
              </Typography>
            </Box>
          </Paper>

          <TableContainer
            component={Paper}
            elevation={1}
            sx={{
              borderRadius: '8px',
              overflow: 'auto',
              maxWidth: '100%',
            }}
          >
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Project Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Current Step
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Last Modified
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No projects match your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProjects.map((project) => (
                    <TableRow key={project.projectId} hover>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                        {project.projectName || 'Untitled Project'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(project.status)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getProjectStepLabel(project.projectStep)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                        {project.lastModifiedDate
                          ? new Date(project.lastModifiedDate).toLocaleDateString()
                          : project.createdDate
                            ? new Date(project.createdDate).toLocaleDateString()
                            : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Project">
                          <IconButton
                            size="small"
                            onClick={() => handleViewProject(project)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {project.isPublished ? (
                          <Tooltip title="Unpublish (demote to project only)">
                            <IconButton
                              size="small"
                              onClick={() => handleUnpublish(project)}
                            >
                              <UnpublishedIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Publish to dashboard">
                            <IconButton
                              size="small"
                              onClick={() => handlePublishClick(project)}
                            >
                              <PublishIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete Project">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteProject(project)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Dialog open={publishDialogOpen} onClose={handlePublishDialogClose} maxWidth="sm" fullWidth>
            <DialogTitle>Publish to dashboard</DialogTitle>
            <DialogContent>
              {publishProject && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Publish &quot;{publishProject.projectName || 'Untitled Project'}&quot; as a dashboard. Select at least one folder (every dashboard must be in at least one folder). Newly created folders are selected for you; add or remove folders below before you publish.
                </Typography>
              )}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Create new folder
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel id="publish-folder-parent-label">Parent location</InputLabel>
                <Select
                  labelId="publish-folder-parent-label"
                  value={publishCreateParentId}
                  label="Parent location"
                  onChange={(e) => setPublishCreateParentId(e.target.value)}
                  disabled={publishFoldersLoading}
                >
                  <MenuItem value="">
                    <em>My Dashboards (top level)</em>
                  </MenuItem>
                  {publishFoldersFlat.map((f) => (
                    <MenuItem key={f.FolderId} value={f.FolderId}>
                      {f.FullPath || f.FolderPath || f.FolderName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                <TextField
                  size="small"
                  label="New folder name"
                  fullWidth
                  value={publishNewFolderName}
                  onChange={(e) => setPublishNewFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handlePublishCreateFolder();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handlePublishCreateFolder}
                  disabled={
                    !publishNewFolderName.trim()
                    || !publishUserId
                    || publishCreateSubmitting
                    || publishFoldersLoading
                  }
                  sx={{ flexShrink: 0, mt: 0.5 }}
                >
                  {publishCreateSubmitting ? 'Creating…' : 'Create'}
                </Button>
              </Box>
              {!publishUserId && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Loading account for folder creation…
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Select folder(s)
              </Typography>
              {publishFoldersLoading ? (
                <Typography variant="body2" color="text.secondary">Loading folders…</Typography>
              ) : publishFoldersFlat.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No folders yet. Create one above, then select it to publish.
                </Typography>
              ) : (
                <List dense>
                  {publishFoldersFlat.map((f) => (
                    <ListItemButton
                      key={f.FolderId}
                      onClick={() => togglePublishFolder(f.FolderId)}
                      selected={publishFolderIds.includes(f.FolderId)}
                    >
                      <ListItemIcon>
                        <FolderIcon />
                      </ListItemIcon>
                      <ListItemText primary={f.FolderName} secondary={f.FolderPath || f.FullPath} />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handlePublishDialogClose}>Cancel</Button>
              <Button
                onClick={handlePublishSubmit}
                disabled={publishSubmitting || publishFolderIds.length === 0}
                variant="contained"
              >
                {publishSubmitting ? 'Publishing…' : 'Publish'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Container>
  );
};

export default ProjectsPage;
